import { fetchVideoDetails } from '../../api/videos';
import { getAttachmentsDetails } from './NotificationUtils';
import { ACM } from '../../constants/UserRoles';

export const createNotificationHandlers = ({
  user,
  typeOfUser,
  patients,
  studentData,
  teacherData,
  teachersDataforTeacherlogin,
  allAttachments,
  videoList,
  navigate,
  setOpenRightPanel,
  selectPatient,
  markNotificationAsRead,
  setOpenVideoUpload,
}) => {
  const goToPatient = async (patientId) => {
    const patientData = patients.find((each) => each.patientId === patientId);
    if (patientData) {
      setOpenRightPanel(false);
      selectPatient(patientId);
      navigate(`/patient/${patientId}`, {
        state: {
          patientData: {
            ...patientData.patientInfo,
            id: patientData.patientId,
          },
        },
      });
    }
  };

  const getVideoDetails = async (uid, videoId) => {
    const result = await fetchVideoDetails(uid, videoId);
    return result;
  };

  const handleAttachmentOpen = async (notificationDetails) => {
    try {
      // 1. Get the necessary IDs from the notification object
      const attachmentId = notificationDetails?.attachmentID;
      const patientId =
        ACM[typeOfUser]?.isDoctor || ACM[typeOfUser]?.isTeacher
          ? notificationDetails.userId
          : user?.uid;

      if (!attachmentId || !patientId) {
        return;
      }

      // 2. Find the full attachment details using the ID
      const attachment = getAttachmentsDetails(attachmentId, allAttachments);

      if (attachment && attachment?.docData?.info?.url) {
        // 3. Navigate to the /smartsearch route with the required state
        navigate('/smartsearch', {
          state: {
            attachmentId: attachment?.docId,
            documentUrl: attachment?.docData?.info?.url,
            documentName: attachment?.docData?.info?.fileName || 'Document',
            patientId: patientId, // Pass the patient/student ID
          },
        });

        // 4. Mark notification as read and close the panel
        setOpenRightPanel(false);
        markNotificationAsRead(notificationDetails.id);
      }
    } catch (error) {
      console.error('Error handling attachment view:', error);
    }
  };

  const handleViewGoal = (notificationDetails) => {
    // The userId from the notification corresponds to the student/patient ID
    const studentOrPatientId =
      ACM[typeOfUser]?.isDoctor || ACM[typeOfUser]?.isTeacher
        ? notificationDetails.userId
        : user?.uid;

    if (studentOrPatientId) {
      navigate('/pendinggoals', {
        state: { studentId: studentOrPatientId },
      });

      // Mark notification as read and close the side panel
      setOpenRightPanel(false);
      markNotificationAsRead(notificationDetails.id);
    } else {
      console.error(
        'User ID not found in notification for goal:',
        notificationDetails
      );
    }
  };

  const handleVideoOpen = async (notificationDetails) => {
    const uid =
      ACM[typeOfUser]?.isDoctor || ACM[typeOfUser]?.isTeacher
        ? notificationDetails.userId
        : user?.uid;
    const videoId = notificationDetails?.videoID;
    const videoDetails = await getVideoDetails(uid, videoId);
    const doctor = user?.doctorDetails?.find(
      (x) => x.email === notificationDetails?.sentBy
    );
    const patient = patients?.find((x) => x.patientId === uid);

    navigate('/play-video', {
      state: {
        videoDetails,
        // patientID: uid,
        videoSource: 'notification',
        typeOfUser,
        patientID: patient || null,
        videoList: videoList?.map((video) =>
          video.user ? video : { ...video, user }
        ),
        patientName: patient?.patientInfo?.fullname || doctor?.fullname,
        patientPhoto: patient?.patientInfo?.photoURL || doctor?.photoURL,
      },
    });
  };

  const navigateToChatScreen = async (notificationDetails) => {
    // Check if navigate is available
    if (!navigate) {
      return;
    }

    const doctor = user?.doctorDetails?.find(
      (x) => x.email === notificationDetails?.sentBy
    );
    const patient = patients?.find(
      (x) => x.patientId === notificationDetails?.userId
    );
    const teacher = teacherData?.find(
      (x) => x.email === notificationDetails?.sentBy
    );

    // Find teacher by UID for teacher-to-teacher messages
    const teacherToTeacher = teachersDataforTeacherlogin?.find(
      (x) =>
        x.uid === notificationDetails?.userId ||
        x.email === notificationDetails?.sentBy
    );

    const student = studentData?.find(
      (x) => (x.student?.id || x.id) === notificationDetails?.userId
    );
    const { notificationSource, chatId } = notificationDetails;

    if (notificationSource === 'groupMessage') {
      if (chatId) {
        // Set session flag to prevent auto-selection of student list when navigating to group chat
        sessionStorage.setItem('fromNotification', 'group');
        navigate('/message', { state: { groupId: chatId } });
      }
    } else if (notificationSource === 'patientMessage') {
      navigate('/message', { state: { patientId: patient?.patientId } });
    } else if (notificationSource === 'doctorMessage') {
      navigate('/message', { state: { doctorId: doctor?.doctorId } });
    } else if (
      ACM[typeOfUser]?.isTeacher &&
      notificationSource === 'teacherMessage'
    ) {
      // Use UID from teacher data or fallback to userId from notification
      const teacherId =
        teacherToTeacher?.uid ||
        teacherToTeacher?.id ||
        notificationDetails?.userId;

      if (teacherId) {
        // Set session flag for teacher-to-teacher navigation from notification
        sessionStorage.setItem('fromNotificationTeacherToTeacher', teacherId);
        navigate('/message', { state: { teacherId } });
      }
    } else if (
      ACM[typeOfUser]?.isStudent &&
      notificationSource === 'teacherMessage'
    ) {
      navigate('/message', { state: { teacherId: teacher?.id } });
    } else if (notificationSource === 'studentMessage') {
      navigate('/message', { state: { studentId: student?.id } });
    }
  };

  const openDescriptionModel = () => {
    setOpenRightPanel(false);
    setOpenVideoUpload(false);
  };

  return {
    goToPatient,
    handleAttachmentOpen,
    handleViewGoal,
    handleVideoOpen,
    navigateToChatScreen,
    openDescriptionModel,
  };
};
